import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Dimensions,
  Animated,
  Platform,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { hasSuperUserAccess } from '../utils/sessionUser';
import { canSeeGradingTile } from '../utils/gradingAccess';
import { getGradingTileImageSource } from '../utils/gradingTileImage';
import { getClassBuilderTileImageSource } from '../utils/classBuilderTileImage';
import { canAccessBuilderScreen } from '../utils/builderAccess';
import CursorPointerIcon from '../components/CursorPointerIcon';

const { width } = Dimensions.get('window');
const TILE_WIDTH = (width - 60) / 2; // 2 columns with padding

// Local assets
const BREAKS_BANNER_IMAGE = require('../../assets/IMG_7071.PNG');
const COURSES_IMAGE = require('../../assets/ChatGPT Image Jan 1, 2026, 02_28_17 AM.png');
const ASSIGNMENTS_BUILDER_IMAGE = require('../../assets/belts images/2nd degree black belt image.png');

const builderMenuItems = [
  { id: 1, title: 'Exercise Builder', icon: '📝', navigation: 'ExerciseBuilder' },
  { id: 2, title: 'Exercise Type Builder', icon: '📋', navigation: 'ExerciseTypeBuilder' },
  { id: 3, title: 'Course Builder', icon: '📚', navigation: 'CourseBuilder' },
  { id: 4, title: 'Quotes Builder', icon: '💬', navigation: 'QuotesBuilder' },
  { id: 5, title: 'Upload Existing Breaks', icon: '⬆️', navigation: 'BreaksBuilder' },
  { id: 6, title: 'Breaks Builder', imageLocal: BREAKS_BANNER_IMAGE, navigation: 'CreateBreaksBuilder' },
  {
    id: 7,
    title: 'Story Builder',
    imageLocal: require('../../assets/story_builder_script_flaticon.png'),
    navigation: 'StoryBuilder',
  },
  {
    id: 8,
    title: 'Course Editor',
    imageLocal: COURSES_IMAGE,
    navigation: 'CourseEditor',
    overlayPencil: true,
  },
  {
    id: 9,
    title: 'Assignments Builder',
    imageLocal: ASSIGNMENTS_BUILDER_IMAGE,
    navigation: 'AssignmentsBuilder',
    overlayCursor: true,
  },
  {
    id: 10,
    title: 'Routine Builder',
    imageLocal: require('../../assets/Routines image.png'),
    navigation: 'RoutineBuilder',
  },
  {
    id: 11,
    title: 'Record Page Layout',
    icon: '📐',
    navigation: 'RecordPageLayoutBuilder',
    requiresSuperUser: true,
  },
  {
    id: 12,
    title: 'Grading',
    ...getGradingTileImageSource(),
    navigation: 'ChangeGrade',
    requiresGradingAccess: true,
  },
  {
    id: 13,
    title: 'Create/Edit Users',
    ...getGradingTileImageSource(),
    navigation: 'CreateUser',
    requiresSuperUser: true,
  },
  {
    id: 14,
    title: 'Class Builder',
    ...getClassBuilderTileImageSource(),
    navigation: 'ClassBuilder',
  },
  {
    id: 15,
    title: 'Dim mak builder',
    imageLocal: require('../../assets/dim-mak-builder-tile.png'),
    navigation: 'DimMakBuilder',
  },
];

export default function BuilderMainPage({ navigation, route }) {
  const theme = useTheme();
  const scaleAnim = useRef({}).current;
  const [currentUser, setCurrentUser] = useState(null);
  const stopwatchRef = useRef(null);

  useEffect(() => {
    // Fetch current user data
    const userId = route?.params?.userId || 1;
    fetch(`${getApiUrl()}/api/users/${userId}`)
      .then(res => res.json())
      .then(data => setCurrentUser(data))
      .catch(err => console.error('Error fetching user:', err));
  }, [route]);

  // Initialize scale animations for each tile
  builderMenuItems.forEach(item => {
    if (!scaleAnim[item.id]) {
      scaleAnim[item.id] = new Animated.Value(1);
    }
  });

  const handleTilePress = (item) => {
    if (item.requiresSuperUser && !hasSuperUserAccess(currentUser)) {
      return;
    }
    if (item.requiresGradingAccess && !canSeeGradingTile(currentUser)) {
      return;
    }
    if (item.navigation && !canAccessBuilderScreen(item.navigation, currentUser)) {
      return;
    }
    // Animate press feedback
    Animated.sequence([
      Animated.timing(scaleAnim[item.id], {
        toValue: 0.95,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(scaleAnim[item.id], {
        toValue: 1,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start();

    // Navigate to builder screen
    if (item.navigation) {
      navigation.navigate(item.navigation, {
        userId: currentUser?.id || 1,
        user: currentUser,
      });
    }
  };

  const styles = getStyles(theme);

  return (
    <View style={styles.container}>
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Builders</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      {/* Main Content */}
      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.tilesContainer}>
          {builderMenuItems
            .filter((item) => {
              if (item.requiresSuperUser && !hasSuperUserAccess(currentUser)) return false;
              if (item.requiresGradingAccess && !canSeeGradingTile(currentUser)) return false;
              if (item.navigation && !canAccessBuilderScreen(item.navigation, currentUser)) {
                return false;
              }
              return true;
            })
            .map((item, index) => (
            <Animated.View
              key={item.id}
              style={[
                {
                  transform: [{ scale: scaleAnim[item.id] || 1 }],
                },
                index % 2 === 0 && styles.tileLeft,
                index % 2 === 1 && styles.tileRight,
              ]}
            >
              <TouchableOpacity
                style={styles.tile}
                onPress={() => handleTilePress(item)}
                activeOpacity={0.8}
              >
                {item.imageLocal ? (
                  item.overlayPencil || item.overlayCursor ? (
                    <View style={styles.tileImageWrap}>
                      <Image
                        source={item.imageLocal}
                        style={styles.tileImageFill}
                        contentFit="contain"
                      />
                      <View style={styles.tilePencilBadge} pointerEvents="none">
                        {item.overlayCursor ? (
                          <CursorPointerIcon size={18} />
                        ) : (
                          <Text style={styles.tilePencilEmoji}>✏️</Text>
                        )}
                      </View>
                    </View>
                  ) : (
                    <Image
                      source={item.imageLocal}
                      style={styles.tileImage}
                      contentFit="contain"
                    />
                  )
                ) : item.imageUrl ? (
                  <Image
                    source={{ uri: item.imageUrl }}
                    style={styles.tileImage}
                    contentFit="contain"
                  />
                ) : (
                  <Text style={styles.tileIcon}>{item.icon}</Text>
                )}
                <Text style={styles.tileTitle}>{item.title}</Text>
                {item.subtitle && (
                  <Text style={styles.tileSubtitle}>{item.subtitle}</Text>
                )}
              </TouchableOpacity>
            </Animated.View>
          ))}
        </View>
      </ScrollView>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: 'transparent',
  },
  hamburgerContainer: {
    marginRight: 15,
    backgroundColor: 'transparent',
  },
  breadcrumbContainer: {
    flex: 1,
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  profileIconContainer: {
    backgroundColor: 'transparent',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
  },
  tilesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  tile: {
    width: TILE_WIDTH,
    height: 180,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: theme.isDarkMode ? 0 : 12,
    padding: 20,
    marginBottom: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: theme.isDarkMode ? 2 : 3,
    borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
    ...(Platform.OS === 'web' ? {
      boxShadow: theme.isDarkMode ? 'none' : '0 2px 8px rgba(212, 175, 55, 0.3)',
    } : {
      shadowColor: theme.isDarkMode ? 'transparent' : '#d4af37',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: theme.isDarkMode ? 0 : 0.3,
      shadowRadius: 4,
      elevation: theme.isDarkMode ? 0 : 4,
    }),
  },
  tileLeft: {
    marginRight: 10,
  },
  tileRight: {
    marginLeft: 10,
  },
  tileIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  tileImage: {
    width: '100%',
    height: 80,
    marginBottom: 12,
  },
  tileImageWrap: {
    width: '100%',
    height: 80,
    marginBottom: 12,
    position: 'relative',
  },
  tileImageFill: {
    width: '100%',
    height: '100%',
  },
  tilePencilBadge: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    backgroundColor: 'rgba(255,255,255,0.94)',
    borderRadius: 16,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(212,175,55,0.65)',
  },
  tilePencilEmoji: {
    fontSize: 15,
  },
  tileTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    textAlign: 'center',
    marginBottom: 4,
  },
  tileSubtitle: {
    fontSize: 12,
    color: '#d4af37',
    textAlign: 'center',
    marginTop: 4,
  },
});
